import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTimes, FaSpinner, FaRobot, FaBug, FaShieldAlt, FaExclamationTriangle } from 'react-icons/fa';

const SEVERITY_STYLES = {
  low:      { badge: 'bg-green-100 text-green-800 border border-green-200',  dot: 'bg-green-500'  },
  medium:   { badge: 'bg-yellow-100 text-yellow-800 border border-yellow-200', dot: 'bg-yellow-500' },
  high:     { badge: 'bg-orange-100 text-orange-800 border border-orange-200', dot: 'bg-orange-500' },
  critical: { badge: 'bg-red-100 text-red-800 border border-red-200',         dot: 'bg-red-500'    },
};

const CONFIDENCE_LABEL = { low: 'Low confidence', medium: 'Medium confidence', high: 'High confidence' };

export default function DiagnosticsModal({ isOpen, onClose, androidId, deviceName }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [result, setResult]   = useState(null);
  const API_BASE = process.env.REACT_APP_API_SERVER_URL;

  useEffect(() => {
    if (isOpen && androidId) {
      runDiagnostics();
    }
    // Reset when closed
    if (!isOpen) {
      setResult(null);
      setError(null);
    }
  }, [isOpen, androidId]); // eslint-disable-line react-hooks/exhaustive-deps

  const runDiagnostics = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await axios.post(`${API_BASE}/maiApi/diagnose`, { deviceId: androidId });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Diagnostic failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const diagnosis = result?.diagnosis;
  const severity  = diagnosis?.severity?.toLowerCase();
  const severityStyle = SEVERITY_STYLES[severity] || SEVERITY_STYLES.medium;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FaRobot className="text-indigo-500 text-lg" />
            <div>
              <h2 className="text-sm font-semibold text-gray-800">AI Device Diagnostics</h2>
              <p className="text-xs text-gray-500">{deviceName || androidId}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {diagnosis && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${severityStyle.badge}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${severityStyle.dot}`} />
                {severity?.toUpperCase()} SEVERITY
              </span>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <FaSpinner className="animate-spin text-3xl text-indigo-400 mb-3" />
              <p className="text-sm font-medium">Analyzing last 24 hours of telemetry…</p>
              <p className="text-xs mt-1">This may take a few seconds</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
              <FaExclamationTriangle className="text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-700">Diagnostic failed</p>
                <p className="text-xs text-red-600 mt-0.5">{error}</p>
                <button
                  onClick={runDiagnostics}
                  className="text-xs text-red-600 underline mt-2 hover:text-red-800"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          {diagnosis && !loading && (
            <div className="space-y-4">

              {/* Meta row */}
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span>{result.dataPointCount} telemetry points analysed</span>
                <span>·</span>
                <span>{CONFIDENCE_LABEL[diagnosis.confidence?.toLowerCase()] || 'Unknown confidence'}</span>
              </div>

              {/* Root Cause */}
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <FaBug className="text-indigo-500 text-xs" />
                  <h3 className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Root Cause</h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{diagnosis.rootCause}</p>
              </div>

              {/* Anomalies */}
              <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <FaExclamationTriangle className="text-yellow-500 text-xs" />
                  <h3 className="text-xs font-semibold text-yellow-700 uppercase tracking-wide">
                    Anomalies Found ({diagnosis.anomalies?.length || 0})
                  </h3>
                </div>
                {diagnosis.anomalies?.length ? (
                  <ul className="space-y-1.5">
                    {diagnosis.anomalies.map((anomaly, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-yellow-400 flex-shrink-0" />
                        {anomaly}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 italic">No anomalies detected</p>
                )}
              </div>

              {/* Recommended Actions */}
              <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <FaShieldAlt className="text-green-500 text-xs" />
                  <h3 className="text-xs font-semibold text-green-700 uppercase tracking-wide">Recommended Actions</h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{diagnosis.prevention}</p>
              </div>

            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex justify-between items-center">
          <p className="text-[10px] text-gray-400">Powered by Claude · Last 24h of telemetry</p>
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
}
